import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchFunnelPacket, fetchLmiZoneLookup, updateOriginationSource } from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { uploadLMIProofDoc } from '~/services/upload-lmi-proof-doc';
import { INTERNAL_ROUTES } from '~/utils/constants';
import { store } from '~/utils/store';
import { sendToSegment, trackError, updateTrackingIdentity } from '~/utils/tracking';
import {
  LMIProofDoc,
  LMIProofDocsUpload,
  LMIProofDocsUploadProps,
  UploadContext,
} from './component/lmi-proof-docs-upload.component';

const trackStarted = async (userId: number) => {
  const funnelPacket = await fetchFunnelPacket({ userId });
  sendToSegment({
    type: 'upload_lmi_proof_docs_step_started',
    funnelPacket,
  });
};

const trackCompleted = context => {
  sendToSegment({
    type: 'upload_lmi_proof_docs_step_completed',
    funnelPacket: context.funnelPacket,
    documentTypes: context.documentTypes,
  });
};

const overrideCopy = () => ({
  description: (
    <span>
      We need to verify that you qualify as a low- or moderate-income household. You can upload
      proof of participation (supported formats: PNG, JPEG, HEIC) in any of the following programs.
    </span>
  ),
  saveContinue: 'Save and continue',
  skip: 'Skip this step',
  title: 'Add your income eligibility documents',
  step: 'Step 3 of 4',
});

export const LMIProofDocsUploadWrapper = ({ onProofDocUpload }) => {
  const userId = store.get('USER_ID')!;
  const [funnelPacket, setFunnelPacket] = useState<FetchFunnelPacketResponse>();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    }
    trackStarted(userId);
    const fetchPacket = async () => {
      try {
        setError(null);
        const funnelPacket = await fetchFunnelPacket({ userId });
        setFunnelPacket(funnelPacket);
        sendToSegment({
          type: 'upload_lmi_proof_docs_step_started',
          funnelPacket: funnelPacket!,
        });
        updateTrackingIdentity(funnelPacket);
        const response = await fetchLmiZoneLookup({
          utilityAccountId: funnelPacket.utilityAccount.id,
        });
        const { isInLmiArea, error } = response;
        if (error) {
          store.set('IS_IN_LMI_ZONE', false);
        }
        store.set('IS_IN_LMI_ZONE', isInLmiArea);
      } catch (e) {
        setError(new Error('Something went wrong. Please try again.'));
        trackError(e);
      }
    };
    fetchPacket();
  }, [userId]);

  const handleNext = async (docs: LMIProofDoc[] = [], onProofDocUpload) => {
    await saveFile(docs, funnelPacket!, onProofDocUpload);
    if (docs.length === 0) {
      await updateOriginationSource(store.get('USER_ID'), 'sponsored-solar', undefined, {
        proofDocSkipped: true,
      });
    }
  };

  const uploadFilesSequentially = async (docs: LMIProofDoc[], utilityAccountId: number) => {
    let results: LMIProofDoc[] = [];
    for (const doc of docs) {
      let result: LMIProofDoc = {} as LMIProofDoc;
      try {
        const { documentType, image } = doc as LMIProofDoc;
        result = await uploadLMIProofDoc({
          utilityAccountId,
          documentType,
          file: image,
          base64Image: image.base64Image!,
        });

        if (result.image.error) {
          throw new Error(result.image.error);
        }
        results.push(result);
      } catch (error) {
        results = [];
        throw new Error(error);
      }
    }
    return results;
  };

  const saveFile = async (
    docs: LMIProofDoc[],
    funnelPacket: FetchFunnelPacketResponse,
    onProofDocUpload,
  ) => {
    const utilityAccountId = funnelPacket?.utilityAccount.id;
    setLoading(true);
    const results = await uploadFilesSequentially(docs, utilityAccountId);
    setLoading(false);

    if (results.length === docs.length) {
      const context = { documentTypes: docs.filter(doc => doc.documentType), funnelPacket };
      trackCompleted(context);
      onProofDocUpload();
    }
  };

  const props: LMIProofDocsUploadProps = {
    context: {
      funnelPacket: funnelPacket ?? ({} as UploadContext['funnelPacket']),
      documentTypes: [],
      docs: [],
    },
    next: docs => handleNext(docs, onProofDocUpload),
    skip: docs => handleNext(docs, onProofDocUpload),
    flags: {
      showFaqButton: true,
    },
    overrideCopy,
    error,
    loading,
    pending: false,
  };

  return <LMIProofDocsUpload {...props} />;
};
